import type { ReactNode } from "react";

import { Card, PageHead, Pill, Section, Table, Td, Th } from "@/components/kit";

import styles from "./docs.module.css";

/**
 * Does the design actually hold?
 *
 * Four sports pushed through it in detail — boxing, cycling, horse racing and
 * cricket — checking master data, every competition shape, every kind of
 * fixture, and who gets appointed to one.
 *
 * The job here is to break the design, not to confirm it. Six things did break,
 * and they are listed at the bottom with what each costs to fix.
 */

type Verdict = "good" | "warn" | "bad";

const VERDICT_WORD: Record<Verdict, string> = {
  good: "works",
  warn: "works, with care",
  bad: "breaks",
};

function Mark({ v }: { v: Verdict }) {
  return (
    <Pill tone={v} dot={v !== "good"}>
      {VERDICT_WORD[v]}
    </Pill>
  );
}

function Code({ label, children }: { label?: string; children: string }) {
  return (
    <div>
      {label && <p className={styles.codeLabel}>{label}</p>}
      <pre className={styles.code}>{children}</pre>
    </div>
  );
}

function Rows({
  head,
  rows,
}: {
  head: string[];
  rows: [string, string, string, Verdict][];
}) {
  return (
    <Table>
      <thead>
        <tr>
          {head.map((h) => (
            <Th key={h}>{h}</Th>
          ))}
          <Th>Holds?</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([a, b, c, v]) => (
          <tr key={a}>
            <Td strong>{a}</Td>
            <Td>{b}</Td>
            <Td quiet>{c}</Td>
            <Td>
              <Mark v={v} />
            </Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function Sport({
  name,
  why,
  children,
}: {
  name: string;
  why: string;
  children: ReactNode;
}) {
  return (
    <Section title={name} note={why}>
      <div className={styles.stack}>{children}</div>
    </Section>
  );
}

/* ------------------------------------------------------------------ boxing -- */

const BOXING_MASTERS: [string, string, string, Verdict][] = [
  ["Boxer", "participant", "stance, reach, current division", "good"],
  ["Promoter", "participant", "an organisation, not a person", "good"],
  ["Manager, trainer, cornerman", "participant", "people attached to a boxer", "good"],
  ["Judge, referee", "participant", "people attached to the bout", "good"],
  ["Sanctioning body", "participant", "WBA, WBC, IBF, WBO", "good"],
  ["Weight division", "outcome-codes", "a vocabulary, not a party", "good"],
  ["Venue", "venue", "shared across sports", "good"],
  [
    "Title / belt",
    "outcome-codes",
    "a vocabulary the bout points at — see below, this was the one thing you fixed",
    "good",
  ],
];

const BOXING_TREE = `A fight card, which is the normal case

  Fight card  ("Riyadh Season, 21 Sept")
    └ Bout    main event
    └ Bout    chief support
    └ Bout    undercard …

  levels:  card   canBeTop yes   holds []   hasFixtures yes


A tournament, which is where it breaks

  World Boxing Super Series
    └ Quarter-finals
        └ Bout          ← this bout is ALSO on a fight card
    └ Semi-finals
    └ Final

  A bout has one parent. It needs two: the card it was
  fought on, and the round it advances out of.`;

const BOXING_FIXTURE = `Bout            DUEL

  entrant       boxer, exactly 2
  alignments    ["Red corner", "Blue corner"]
  periods       round · count 12 · ownedBy fixture
  format        weight limit, rounds
  meta          titles      ref → belt, several allowed
                gate, purse, broadcaster
  resultFields  method, round stopped, time stopped
  statuses      WIN · LOSS · DRAW · NC · DQ

  configs       Pro title      12 rounds × 3 min
                Pro non-title  10 rounds × 3 min
                Women's        10 rounds × 2 min
                Amateur         3 rounds × 3 min`;

const BOXING_PEOPLE: [string, string, string, Verdict][] = [
  ["Judge", "official · min 3 · max 3", "a panel, not one person", "good"],
  ["Referee", "official · min 1 · max 1", "", "good"],
  ["Trainer", "connection · min 0 · max 3", "attached to one boxer, not the bout", "good"],
  ["Promoter", "connection · min 0 · max 2", "co-promotions are normal", "good"],
  [
    "Judge's scorecard",
    "no shape fits",
    "three judges × twelve rounds × two boxers is a grid, not a list of fields",
    "bad",
  ],
];

/* ----------------------------------------------------------------- cycling -- */

const CYCLING_MASTERS: [string, string, string, Verdict][] = [
  ["Rider", "participant", "", "good"],
  ["Trade team", "participant", "who they ride for all year", "good"],
  ["National team", "participant", "who they ride for at the Worlds", "warn"],
  ["Commissaire", "participant", "the officials", "good"],
  ["Team manager, directeur sportif", "participant", "", "good"],
  ["Race, course", "competition / venue", "a route rather than a stadium", "good"],
];

const CYCLING_TREE = `A grand tour

  Tour de France 2027
    └ Stage 1   individual time trial
    └ Stage 2   road stage
    └ Stage 9   team time trial     ← entrant is a TEAM, not a rider
    …
  Overall, points, mountains and youth are folded
  across the stages — standings, not fixtures.

A one-day classic

  Paris–Roubaix 2027       no parent at all

The Worlds

  UCI Road World Championships
    └ Men's road race       riders enter for their COUNTRY,
                            not their trade team`;

const CYCLING_FIXTURE = `Road stage        FIELD · entrant rider
  entryFields     bib number
  resultFields    time, gap
  rankings        stage · overall · points · mountains · youth

Individual TT     FIELD · entrant rider
  entryFields     start time

Team TT           FIELD · entrant TRADE TEAM
  composition     riders, min 4 max 8, ordered

  → one competition, three kinds of fixture,
    two different things entering.`;

/* ----------------------------------------------------------- horse racing -- */

const RACING_MASTERS: [string, string, string, Verdict][] = [
  ["Horse", "participant", "", "good"],
  ["Jockey, trainer", "participant", "", "good"],
  ["Owner", "participant", "often a syndicate of many people", "good"],
  ["Racecourse", "venue", "", "good"],
  ["Going, race class", "outcome-codes", "vocabularies", "good"],
  [
    "Sire and dam",
    "two ref fields on the horse",
    "a ref field can point at any master including its own — no new anything",
    "good",
  ],
];

const RACING_TREE = `A normal day

  Cheltenham Festival 2027       a meeting of meetings
    └ Day 1                      a meeting
        └ 13:30 Supreme Novices  a race
        └ 14:10 Arkle
    └ Day 2 …

A single fixture

  Newmarket, 4 May               a meeting
    └ 15:40 Handicap             a race

The Triple Crown

  2000 Guineas · The Derby · St Leger
  — three races at three courses, run by three different
    people, grouped only by a name. Solved by the
    "belongs to, without being inside" link.`;

const RACING_FIXTURE = `Race              FIELD · entrant horse, 2 to 40

  entryFields     stall / draw, weight carried, official rating,
                  headgear, age, silks
  resultFields    finishing position, distance beaten,
                  starting price, time
  statuses        WON · PL · UR · PU · F · BD · RR · DIS
  meta            going, ground description, prize fund
  rankings        overall

  configs         Flat · Hurdle · Chase · NH Flat · All-weather
                  (obstacles, distance units, weight rules)`;

const RACING_PEOPLE: [string, string, string, Verdict][] = [
  ["Jockey", "connection · min 1 · max 1", "on the horse, not on the race", "good"],
  ["Trainer", "connection · min 1 · max 1", "", "good"],
  ["Owner", "connection · min 1 · max —", "a syndicate can be forty people", "good"],
  ["Stewards", "official · min 0 · max —", "", "good"],
];

/* ---------------------------------------------------------------- cricket -- */

const CRICKET_MASTERS: [string, string, string, Verdict][] = [
  ["Team", "participant", "national, franchise and domestic all at once", "good"],
  ["Player", "participant", "at a franchise, a club and a country simultaneously", "warn"],
  ["Umpire, match referee", "participant", "", "good"],
  ["Ground", "venue", "", "good"],
  ["Country", "participant", "listed in no sport, so shared by all", "good"],
  ["Ways of getting out", "outcome-codes", "bowled, lbw, run out, retired hurt", "good"],
];

const CRICKET_TREE = `Four shapes, all in one sport

  Tour                          India in Australia 2027
    └ Test series
        └ Match
    └ Match                     a warm-up, no series

  League                        IPL
    └ Season 2027
        └ League stage
            └ Match
        └ Playoffs
            └ Match

  World Cup
    └ Group stage → Match
    └ Super Eights → Match
    └ Knockout    → Match

  A single match                a one-off exhibition, no parent

The World Test Championship folds matches from many
separate bilateral series into one table — a standing,
and standings are already allowed to span competitions.`;

const CRICKET_FIXTURE = `Match             DUEL · entrant team, exactly 2

  composition     player · min 11 · max 15 · ordered
                  roles: captain, wicketkeeper,
                         substitute, impact player
  alignments      ["Home", "Away"]
  periods         innings · ownedBy ENTRY · count 1 or 2
                    └ over
  format          overs, ballsPerOver, inningsPerSide, days
  meta            match number, toss winner, toss decision,
                  attendance, pitch report
  statuses        the sport's own ways of getting out

  configs         T20 · ODI · Test · The Hundred · T10
                  The Hundred drops "overs" entirely and adds
                  balls and ballsPerSet — a config that changes
                  the shape, not only the numbers.`;

const CRICKET_PEOPLE: [string, string, string, Verdict][] = [
  ["On-field umpire", "official · min 2 · max 2", "", "good"],
  ["TV umpire", "official · min 1 · max 1", "its own role, so its own slot", "good"],
  ["Fourth umpire", "official · min 0 · max 1", "", "good"],
  ["Match referee", "official · min 1 · max 1", "", "good"],
  ["Coach", "connection · min 0 · max —", "attached to a side, not to the match", "good"],
];

/* ------------------------------------------------------------------ found -- */

const FOUND: [string, string, string, Verdict][] = [
  [
    "A tournament bout on a mixed card",
    "Boxing",
    "It sits on the card it was fought on and in the round it advances out of. One sport, one rare shape, and two workarounds — see below. Not worth a new link table.",
    "warn",
  ],
  [
    "A period count can be a maximum",
    "Cricket",
    "A Test is up to two innings each — fewer with a follow-on, or if it ends early. Count is a fixed number.",
    "warn",
  ],
  [
    "A level holds several kinds of fixture",
    "Cycling",
    "A grand tour holds road stages, time trials and a team time trial, with different things entering. Allowed, but a single fixture-kind column on a competition reads like a rule.",
    "warn",
  ],
];

/** Findings that turned out to be mine rather than the design's. Kept, because
 * a validation that only records what broke is not a validation. */
const WITHDRAWN: [string, string, string, Verdict][] = [
  [
    "Sire and dam have no home",
    "Horse racing",
    "Two ref fields. A ref may name any master, its own included.",
    "good",
  ],
  [
    "A ref field cannot hold several",
    "Boxing",
    "An array field whose items are refs. Four belts on one bout, today.",
    "good",
  ],
  [
    "A scorecard grid cannot be declared",
    "Boxing",
    "An array of objects — round, red score, blue score. It can. (Entering them is still the scorer UI's job.)",
    "good",
  ],
  [
    "Football matches have two parents",
    "Football",
    "They do not. A match is in exactly one competition; coefficients and qualifying feeds are standings.",
    "good",
  ],
];

export function ValidatePage() {
  return (
    <div className={styles.page}>
      <PageHead
        title="Does the design hold?"
        note="Boxing, cycling, horse racing and cricket pushed through it in detail — masters, every competition shape, every kind of fixture, and everyone who gets appointed to one. The job was to break it."
      />

      <Section title="The short answer">
        <div className={styles.claim}>
          <p>
            <strong>It holds. Nothing breaks, and nothing new has to be built for these four.</strong>{" "}
            Configs, levels, records listing their sports, the Games and squads all survived. Three
            things are worth being careful about, and none of them needs a table.
          </p>
          <p>
            <strong>This page first claimed five problems. Four were mine.</strong> Belts, sire and
            dam, a field holding several values, and a scorecard grid are all expressible today —
            three of them because the field catalog is richer than I credited. Football was cited as
            needing two parents and simply does not. They are listed at the bottom rather than
            quietly deleted.
          </p>
          <p>
            <strong>Your question, directly:</strong> selecting one or more coach or official
            already works, and has since the fixture-types work. Every appointment declares a{" "}
            <span className={styles.mono}>min</span> and a <span className={styles.mono}>max</span>.
            Three boxing judges is min 3 max 3. A racehorse syndicate of forty owners is min 1 max
            none. A coach is min 0 max none. The rule that catches people out is one slot per role —
            so cricket&apos;s on-field, TV and fourth umpires are three separate slots, not one slot
            holding four people.
          </p>
          <p>
            <strong>Your question first:</strong> selecting one or more coach or official already
            works, and has since the fixture-types work. Every appointment declares a{" "}
            <span className={styles.mono}>min</span> and a <span className={styles.mono}>max</span>.
            Three boxing judges is min 3 max 3. A racehorse syndicate of forty owners is min 1 max
            none. A coach is min 0 max none. The rule that catches people out is one slot per role —
            so cricket&apos;s on-field, TV and fourth umpires are three separate slots, not one slot
            holding four people.
          </p>
        </div>
      </Section>

      <Sport
        name="Boxing"
        why="The sport with no league and no table. Everything hangs off a single night's card, which is unlike every other sport we have modelled."
      >
        <Rows head={["What it keeps", "Lands in", "Notes"]} rows={BOXING_MASTERS} />
        <Code label="how its competitions are shaped">{BOXING_TREE}</Code>
        <Code label="its one kind of fixture, and its configs">{BOXING_FIXTURE}</Code>
        <Rows head={["Who is appointed", "How it is declared", "Notes"]} rows={BOXING_PEOPLE} />
        <div className={styles.flag}>
          <p>
            <strong>The one awkward shape.</strong> A World Boxing Super Series quarter-final sits
            on the card it was fought on, alongside four fights that have nothing to do with the
            tournament, and in the round it advances out of. Two containers, and a fixture has one
            parent.
          </p>
          <p>
            <strong>This was first written up as a break, and it is not one.</strong> Checking the
            other sports, nothing else needs it: cricket&apos;s World Test Championship, cycling&apos;s
            UCI points and tennis&apos;s race are all <em>standings</em>, which are already allowed
            to gather fixtures from many competitions. Football was cited too, and that was simply
            wrong — a football match is in exactly one competition.
          </p>
          <p>
            So it is one sport, in one rare shape. Two ways out, neither costing a table: make the
            tournament the parent and record the card on the fixture, or make the card the parent
            and express the bracket as links between fixtures — the winner of this one meets the
            winner of that one. The second is arguably more honest anyway, since a knockout draw
            really is edges between fixtures rather than a box around them.
          </p>
        </div>
        <div className={styles.claim}>
          <p>
            <strong>Belts: solved, by you.</strong> A title is a vocabulary — a short list the sport
            declares, like the ways a cricketer can be out — and the bout carries a{" "}
            <span className={styles.mono}>ref</span> to it in{" "}
            <span className={styles.mono}>meta</span>, holding several at once for a unification.
          </p>
          <p>
            <strong>On the bout, not the card.</strong> A card usually has more than one title
            fight, for different belts and different divisions. Put it on the card and the WBC
            heavyweight belt lands on the four-rounder that opened the night as well.
          </p>
          <p>
            A <span className={styles.mono}>ref</span> rather than typed text, so “WBC World
            Heavyweight” and “WBC Heavyweight” cannot become two different belts — and so “every
            bout for this title” is a question that can be asked. That needs one flag we already had
            on the list: a ref field that may hold several.
          </p>
          <p>
            What it gives up: a belt has no history of its own, so “who has held this since 1990”
            is not answerable. If that is ever wanted, the vocabulary becomes a proper record and
            nothing already entered has to change.
          </p>
        </div>
      </Sport>

      <Sport
        name="Cycling"
        why="The sport where one competition holds three different kinds of contest, and where who you ride for changes depending on the race."
      >
        <Rows head={["What it keeps", "Lands in", "Notes"]} rows={CYCLING_MASTERS} />
        <Code label="how its competitions are shaped">{CYCLING_TREE}</Code>
        <Code label="its kinds of fixture">{CYCLING_FIXTURE}</Code>
        <div className={styles.claim}>
          <p>
            <strong>Cycling passes, and it is the best evidence we have.</strong> A team time trial
            entering teams while the next day&apos;s stage enters riders is exactly the swimming
            problem — individual and relay — which is what the fixture-kinds work was built for. It
            holds without a change.
          </p>
          <p>
            Riding for a trade team all season and a country at the Worlds is the squad change we
            already agreed: belonging can point at a competition, not only at dates.
          </p>
        </div>
      </Sport>

      <Sport
        name="Horse racing"
        why="The sport with the most people attached to each entrant, and the only one where a master record's family matters."
      >
        <Rows head={["What it keeps", "Lands in", "Notes"]} rows={RACING_MASTERS} />
        <Code label="how its competitions are shaped">{RACING_TREE}</Code>
        <Code label="its one kind of fixture, and its configs">{RACING_FIXTURE}</Code>
        <Rows head={["Who is appointed", "How it is declared", "Notes"]} rows={RACING_PEOPLE} />
        <div className={styles.claim}>
          <p>
            <strong>Racing passes.</strong> This was first written up as a break — a horse&apos;s
            sire and dam are horses, and the only links between records were “is part of” and “sits
            inside”, neither of which means “descended from”.
          </p>
          <p>
            That was a mistake, and the mistake was confusing two different things. A{" "}
            <em>relation</em> writes real rows: a membership with dates, an edge in a tree. A{" "}
            <em>ref field</em> is one record pointing at another, stored alongside its other
            details. A sire is not a membership. It is a fact about the horse, and a field is
            exactly right for it.
          </p>
          <Code>{`horse
  { "key": "sire", "type": "ref", "target": "master:horse" }
  { "key": "dam",  "type": "ref", "target": "master:horse" }`}</Code>
          <p>
            A ref field may name any master through{" "}
            <span className={styles.mono}>master:&lt;code&gt;</span>, its own included, and nothing
            forbids the loop. This works today, with no change at all.
          </p>
          <p>
            The one thing given up: the link is stored with the horse, so “everything by Galileo” is
            a search rather than a lookup. That is fine until pedigree becomes something people
            browse, and it is covered by the plan to index whichever details a sport says are
            searchable.
          </p>
        </div>
      </Sport>

      <Sport
        name="Cricket"
        why="The most competition shapes of any sport we have looked at, and the sport whose formats made us invent configs in the first place."
      >
        <Rows head={["What it keeps", "Lands in", "Notes"]} rows={CRICKET_MASTERS} />
        <Code label="how its competitions are shaped">{CRICKET_TREE}</Code>
        <Code label="its one kind of fixture, and its configs">{CRICKET_FIXTURE}</Code>
        <Rows head={["Who is appointed", "How it is declared", "Notes"]} rows={CRICKET_PEOPLE} />
        <div className={styles.claim}>
          <p>
            <strong>Cricket passes.</strong> Four competition shapes in one sport, five configs, and
            a squad bigger than the eleven who take the field — that last one works by letting the
            side name up to fifteen and using roles to mark who is playing, who is the impact player
            and who is a substitute.
          </p>
          <p>
            One thing to watch: a Test is “up to two innings each”, not two. We can only declare a
            fixed count.
          </p>
        </div>
      </Sport>

      <Section
        title="What broke"
        note="Nothing, in the end. Three things are worth being careful about. None of them needs a new table."
      >
        <Rows head={["What", "Found in", "Why it matters"]} rows={FOUND} />
      </Section>

      <Section
        title="Withdrawn"
        note="Four findings that turned out to be mine rather than the design's. Kept on the page, because a validation that only records what broke is not a validation."
      >
        <Rows head={["What was claimed", "Sport", "Why it was wrong"]} rows={WITHDRAWN} />
        <div className={styles.claim}>
          <p>
            Three of the four came from the same mistake: underrating the field catalog. It has
            fourteen types, and <span className={styles.mono}>ref</span>,{" "}
            <span className={styles.mono}>array</span> and{" "}
            <span className={styles.mono}>object</span> between them cover a record pointing at
            another record, a field holding several values, and a nested table — which was three of
            my findings.
          </p>
        </div>
      </Section>

      <Section title="What the three remaining ones cost">
        <div className={styles.stack}>
          <Card>
            <div className={styles.claim}>
              <p>
                <strong>A period count that is a maximum.</strong> One extra number beside{" "}
                <span className={styles.mono}>count</span>, so a Test can say “up to two innings
                each” rather than “two”. Half an hour.
              </p>
            </div>
          </Card>
          <Card>
            <div className={styles.claim}>
              <p>
                <strong>A level holding several kinds of fixture.</strong> Nothing to build — it
                already works. It needs writing down, so that nobody builds the competition tree
                treating the fixture kind on a competition as a rule. It is a default for the next
                fixture added there, and a grand tour holding road stages, time trials and a team
                time trial has to stay legal.
              </p>
            </div>
          </Card>
          <Card>
            <div className={styles.claim}>
              <p>
                <strong>The boxing tournament card.</strong> Leave it. One rare shape in one sport,
                with two workarounds that cost nothing. Revisit only if someone actually needs to
                browse a mixed card and a bracket at the same time.
              </p>
            </div>
          </Card>
        </div>
      </Section>
    </div>
  );
}
